import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('authToken');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

export const subscriptionService = {
  // SUBSCRIPTION TYPES (Global Templates)
  
  async getSubscriptionTypes() {
    try {
      const response = await api.get('/subscriptions/types');
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription types:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch subscription types');
    }
  },

  async getSubscriptionType(typeId) {
    try {
      const response = await api.get(`/subscriptions/types/${typeId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription type:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch subscription type');
    }
  },

  async createSubscriptionType(typeData) {
    try {
      const response = await api.post('/subscriptions/types', typeData);
      return response.data;
    } catch (error) {
      console.error('Error creating subscription type:', error);
      throw new Error(error.response?.data?.error || 'Failed to create subscription type');
    }
  },

  async updateSubscriptionType(typeId, updates) {
    try {
      const response = await api.put(`/subscriptions/types/${typeId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating subscription type:', error);
      throw new Error(error.response?.data?.error || 'Failed to update subscription type');
    }
  },

  async deleteSubscriptionType(typeId) {
    try {
      const response = await api.delete(`/subscriptions/types/${typeId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting subscription type:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete subscription type');
    }
  },

  // ORGANIZATION SUBSCRIPTIONS

  async getAllOrganizationSubscriptions() {
    try {
      const response = await api.get('/subscriptions/organizations');
      return response.data;
    } catch (error) {
      console.error('Error fetching organization subscriptions:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch organization subscriptions');
    }
  },

  async getOrganizationSubscriptions(organizationId) {
    try {
      const response = await api.get(`/subscriptions/organizations/${organizationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching organization subscriptions:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch organization subscriptions');
    }
  },

  async assignSubscriptionToOrganization(organizationId, subscriptionTypeId, overrides = {}) {
    try {
      const response = await api.post(`/subscriptions/organizations/${organizationId}/assign`, {
        subscriptionTypeId,
        overrides
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning subscription to organization:', error);
      throw new Error(error.response?.data?.error || 'Failed to assign subscription to organization');
    }
  },

  async updateOrganizationSubscription(subscriptionId, updates = {}) {
    try {
      const response = await api.put(`/subscriptions/organizations/subscriptions/${subscriptionId}`, {
        updates
      });
      return response.data;
    } catch (error) {
      console.error('Error updating organization subscription:', error);
      throw new Error(error.response?.data?.error || error.response?.data?.details || 'Failed to update organization subscription');
    }
  },

  async deleteOrganizationSubscription(subscriptionId) {
    try {
      const response = await api.delete(`/subscriptions/organizations/subscriptions/${subscriptionId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting organization subscription:', error);
      throw new Error(error.response?.data?.error || error.response?.data?.details || 'Failed to delete organization subscription');
    }
  },

  async setDefaultSubscription(organizationId, subscriptionTypeId) {
    try {
      const response = await api.put(`/subscriptions/organizations/${organizationId}/default`, {
        subscriptionTypeId
      });
      return response.data;
    } catch (error) {
      console.error('Error setting default subscription:', error);
      throw new Error(error.response?.data?.error || 'Failed to set default subscription');
    }
  },

  async getDefaultSubscription(organizationId) {
    try {
      const response = await api.get(`/subscriptions/organizations/${organizationId}/default`);
      return response.data;
    } catch (error) {
      console.error('Error getting default subscription:', error);
      throw new Error(error.response?.data?.error || 'Failed to get default subscription');
    }
  },

  async getEffectiveSubscriptionConfig(organizationId, subscriptionTypeId) {
    try {
      const response = await api.get(`/subscriptions/organizations/${organizationId}/effective/${subscriptionTypeId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting effective subscription config:', error);
      throw new Error(error.response?.data?.error || 'Failed to get effective subscription config');
    }
  },

  // SENSOR SUBSCRIPTIONS

  async getAllSensorSubscriptions() {
    try {
      const response = await api.get('/subscriptions/sensors');
      return response.data;
    } catch (error) {
      console.error('Error fetching sensor subscriptions:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch sensor subscriptions');
    }
  },

  async getSensorSubscriptionsByOrganization(organizationId) {
    try {
      const response = await api.get(`/subscriptions/sensors?organizationId=${organizationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sensor subscriptions by organization:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch sensor subscriptions');
    }
  },

  async getSensorSubscription(sensorId) {
    try {
      const response = await api.get(`/subscriptions/sensors?sensorId=${sensorId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sensor subscription:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch sensor subscription');
    }
  },

  async assignSensorSubscription(sensorId, organizationId, subscriptionTypeId) {
    try {
      const response = await api.post(`/subscriptions/sensors/${sensorId}/assign`, {
        organizationId,
        subscriptionTypeId
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning sensor subscription:', error);
      throw new Error(error.response?.data?.error || 'Failed to assign sensor subscription');
    }
  },

  async resetSensorToDefault(sensorId, organizationId) {
    try {
      const response = await api.delete(`/subscriptions/sensors/${sensorId}/assignment`, {
        data: { organizationId }
      });
      return response.data;
    } catch (error) {
      console.error('Error resetting sensor to default:', error);
      throw new Error(error.response?.data?.error || 'Failed to reset sensor to default subscription');
    }
  },

  // UTILITY FUNCTIONS

  async validateSensorLimits(organizationId, subscriptionTypeId) {
    try {
      const response = await api.get(`/subscriptions/organizations/${organizationId}/validate/${subscriptionTypeId}`);
      return response.data;
    } catch (error) {
      console.error('Error validating sensor limits:', error);
      throw new Error(error.response?.data?.error || 'Failed to validate sensor limits');
    }
  },

  async checkServiceHealth() {
    try {
      const response = await api.get('/subscriptions/health');
      return response.data;
    } catch (error) {
      console.error('Error checking subscription service health:', error);
      throw new Error(error.response?.data?.error || 'Failed to check service health');
    }
  }
};

export default subscriptionService;
